import { CustomerPermissionCodes, UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { GeneralPermissionCode } from "@odata/GeneratedEnums";
import PayrollYearOperation from "@pages/payroll/payrollYearOperations/PayrollYearOperation";
import BenefitOverview from "@pages/reports/benefitlOverview/BenefitOverview";
import DeductionOverview from "@pages/reports/deductionOverview/DeductionOverview";
import HealthInsuranceContributionsOverview
    from "@pages/reports/healthInsuranceContributionsOverview/HealthInsuranceContributionsOverview";
import OverviewOfTaxCreditsAndTaxAllowances
    from "@pages/reports/overviewOfTaxCreditsAndTaxAllowances/OverviewOfTaxCreditsAndTaxAllowances";
import PayrollOverview from "@pages/reports/payrollOverview/PayrollOverview";
import SocialSecurityContributionsOverview
    from "@pages/reports/socialSecurityContributionsOverview/SocialSecurityContributionsOverview";
import React, { lazy } from "react";

import {
    BENEFIT_OVERVIEW_ROUTE,
    DEDUCTION_OVERVIEW_ROUTE,
    HEALTH_INSURANCE_CONTRIBUTIONS_OVERVIEW_ROUTE,
    ROUTE_ABSENCE_CATEGORIES,
    ROUTE_ACCOUNTING_SETTINGS,
    ROUTE_ANNUAL_TAX_SETTLEMENT,
    ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST,
    ROUTE_ATTENDANCE,
    ROUTE_BENEFITS,
    ROUTE_DEDUCTIONS,
    ROUTE_ELDP,
    ROUTE_EMPLOYEE_PAYSLIP,
    ROUTE_EMPLOYEES,
    ROUTE_EMPLOYMENT,
    ROUTE_EMPLOYMENT_TEMPLATE,
    ROUTE_EXTRA_SALARIES,
    ROUTE_GOVERNMENT_INSTITUTION,
    ROUTE_HEALTH_INSURANCE_COMPANIES,
    ROUTE_PAYROLL_OVERVIEW,
    ROUTE_PAYROLL_SETTING,
    ROUTE_PAYROLL_YEAR_OPERATIONS,
    ROUTE_PAYSLIP,
    ROUTE_PAYSLIP_OVERVIEW,
    ROUTE_RETIREMENT_SAVINGS_COMPANIES,
    ROUTE_SALARY_ADVANCES,
    ROUTE_SALARY_COMPONENTS,
    ROUTE_SOCIAL_EVENTS,
    ROUTE_SOCIAL_REQUESTS,
    ROUTE_SUBMISSIONS,
    ROUTE_TAXPAYER_DECLARATIONS,
    ROUTE_WORKING_PATTERNS,
    SOCIAL_SECURITY_CONTRIBUTIONS_OVERVIEW_ROUTE,
    TAX_CREDITS_AND_ALLOWANCES_OVERVIEW_ROUTE
} from "../routes";
import FormDynamicEntityRoute from "../routes/FormDynamicEntityRoute";
import FormRoute from "../routes/FormRoute";
import PrivateRoute from "../routes/PrivateRoute";

const AbsenceCategory = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/absenceCategory/AbsenceCategory"));
const HealthInsuranceCompany = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/healthInsuranceCompany/HealthInsuranceCompany"));
const WorkingPatterns = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/workingPatterns/WorkingPatterns"));
const Employment = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/employment/Employment"));
const EmploymentTemplate = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/employmentTemplate/EmploymentTemplate"));
const Employee = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/employee/Employee"));
const SalaryComponent = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/salaryComponent/SalaryComponent"));
const SalaryAdvance = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/salaryAdvance/SalaryAdvance"));
const ExtraSalary = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/extraSalary/ExtraSalary"));
const Attendance = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/attendance/Attendance"));
const Payslip = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/payslip/Payslip"));
const PayslipOverview = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/payslip/PayslipOverview"));
const EmployeePayslip = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/payslip/EmployeePayslip"));
const GovernmentInstitution = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/governmentInstitution/GovernmentInstitution"));
const AccountingSettings = lazy(() => import(/* webpackChunkName: "payroll" */"../pages/payroll/accountingSettings/AccountingSettings"));
const Deduction = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/deduction/Deduction"));
const Benefit = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/benefits/Benefit"));
const TaxpayerDeclaration = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration"));
const PayrollSetting = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/payrollSetting/PayrollSetting"));
const Submission = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/submission/Submission"));
const annualTaxSettlementRequest = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest"));
const annualTaxSettlement = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/annualTaxSettlement/AnnualTaxSettlement"));
const SocialEvents = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/socialEvents/SocialEvents"));
const RetirementSavingsCompany = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/retirementSavingsCompany/RetirementSavingsCompany"));
const Eldp = lazy(() => import(/* webpackChunkName: "payroll" */"@pages/payroll/Eldp/Eldp"));

export function payrollTenantBundle(): React.ReactElement[] {
    return [
        <FormRoute
            path={ROUTE_RETIREMENT_SAVINGS_COMPANIES}
            key={ROUTE_RETIREMENT_SAVINGS_COMPANIES}
            GPs={GeneralPermissionCode.TemplateManagement}
            entityType={EntityTypeName.PrRetirementSavingsCompany}
            page={RetirementSavingsCompany}/>
    ];
}

export default function payrollBundle(): React.ReactElement[] {
    return [
        <FormRoute
            path={ROUTE_PAYROLL_SETTING}
            key={ROUTE_PAYROLL_SETTING}
            CPs={UserPermissionCodes.PrPayrollSetting}
            entityType={EntityTypeName.PrPayrollSetting}
            page={PayrollSetting}/>,
        <FormRoute
            path={ROUTE_ABSENCE_CATEGORIES}
            key={ROUTE_ABSENCE_CATEGORIES}
            CPs={UserPermissionCodes.PrPayrollSetting}
            entityType={EntityTypeName.PrAbsenceCategory}
            page={AbsenceCategory}/>,
        <FormRoute
            path={ROUTE_HEALTH_INSURANCE_COMPANIES}
            key={ROUTE_HEALTH_INSURANCE_COMPANIES}
            CPs={UserPermissionCodes.PrPayrollSetting}
            entityType={EntityTypeName.PrHealthInsuranceCompany}
            page={HealthInsuranceCompany}/>,
        <FormRoute
            path={ROUTE_WORKING_PATTERNS}
            key={ROUTE_WORKING_PATTERNS}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrWorkingPattern}
            page={WorkingPatterns}/>,
        <FormRoute
            path={ROUTE_ELDP}
            key={ROUTE_ELDP}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrEldp}
            page={Eldp}/>,
        <FormRoute
            path={ROUTE_EMPLOYMENT}
            key={ROUTE_EMPLOYMENT}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrEmployment}
            page={Employment}/>,
        <FormRoute
            path={ROUTE_EMPLOYMENT_TEMPLATE}
            key={ROUTE_EMPLOYMENT_TEMPLATE}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrEmploymentTemplate}
            page={EmploymentTemplate}/>,
        <FormRoute
            path={ROUTE_EMPLOYEES}
            key={ROUTE_EMPLOYEES}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrEmployee}
            page={Employee}/>,
        <FormRoute
            path={ROUTE_TAXPAYER_DECLARATIONS}
            key={ROUTE_TAXPAYER_DECLARATIONS}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrTaxpayerDeclaration}
            page={TaxpayerDeclaration}/>,
        <FormRoute
            path={ROUTE_SALARY_COMPONENTS}
            key={ROUTE_SALARY_COMPONENTS}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrSalaryComponent}
            page={SalaryComponent}/>,
        <FormRoute
            path={ROUTE_SALARY_ADVANCES}
            key={ROUTE_SALARY_ADVANCES}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrSalaryAdvance}
            page={SalaryAdvance}/>,
        <FormRoute
            path={ROUTE_EXTRA_SALARIES}
            key={ROUTE_EXTRA_SALARIES}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrExtraSalary}
            page={ExtraSalary}/>,
        <FormRoute
            path={ROUTE_ATTENDANCE}
            key={ROUTE_ATTENDANCE}
            CPs={[UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrAttendance]}
            entityType={EntityTypeName.PrAttendance}
            page={Attendance}/>,
        <FormRoute
            path={ROUTE_DEDUCTIONS}
            key={ROUTE_DEDUCTIONS}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrDeduction}
            page={Deduction}/>,
        <FormRoute
            path={ROUTE_BENEFITS}
            key={ROUTE_BENEFITS}
            CPs={UserPermissionCodes.PrPayroll}
            entityType={EntityTypeName.PrBenefit}
            page={Benefit}/>,
        <FormDynamicEntityRoute
            path={ROUTE_SOCIAL_EVENTS}
            key={ROUTE_SOCIAL_EVENTS}
            CPs={[UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrAttendance]}
            page={SocialEvents}/>,
        <FormDynamicEntityRoute
            path={ROUTE_SOCIAL_REQUESTS}
            key={ROUTE_SOCIAL_REQUESTS}
            CPs={CustomerPermissionCodes.PrPayrollAgenda}
            page={SocialEvents}/>,
        <FormRoute
            path={ROUTE_PAYSLIP}
            key={ROUTE_PAYSLIP}
            entityType={EntityTypeName.PrPayslip}
            page={Payslip}/>,
        <FormRoute
            path={ROUTE_PAYSLIP_OVERVIEW}
            key={ROUTE_PAYSLIP_OVERVIEW}
            entityType={EntityTypeName.PrPayslip}
            CPs={[UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrPayrollAgenda]}
            page={PayslipOverview}/>,
        <PrivateRoute exact
                      path={ROUTE_EMPLOYEE_PAYSLIP}
                      key={ROUTE_EMPLOYEE_PAYSLIP}
                      component={EmployeePayslip}/>,
        <PrivateRoute path={`${ROUTE_EMPLOYEE_PAYSLIP}/:year/:month?`}
                      key={`${ROUTE_EMPLOYEE_PAYSLIP}/detail`}
                      component={EmployeePayslip}/>,
        <PrivateRoute
            path={ROUTE_GOVERNMENT_INSTITUTION}
            key={ROUTE_GOVERNMENT_INSTITUTION}
            CPs={UserPermissionCodes.PrPayrollSetting}
            component={GovernmentInstitution}/>,
        <PrivateRoute
            path={ROUTE_ACCOUNTING_SETTINGS}
            key={ROUTE_ACCOUNTING_SETTINGS}
            CPs={UserPermissionCodes.PrPayrollSetting}
            component={AccountingSettings}/>,
        <FormDynamicEntityRoute
            path={ROUTE_SUBMISSIONS}
            key={ROUTE_SUBMISSIONS}
            CPs={UserPermissionCodes.PrPayroll}
            page={Submission}/>,
        <FormRoute
            path={ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST}
            key={ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST}
            CPs={UserPermissionCodes.PrPayrollSetting}
            entityType={EntityTypeName.PrAnnualTaxSettlementRequest}
            page={annualTaxSettlementRequest}/>,
        <FormRoute
            path={ROUTE_ANNUAL_TAX_SETTLEMENT}
            key={ROUTE_ANNUAL_TAX_SETTLEMENT}
            CPs={UserPermissionCodes.PrPayrollSetting}
            entityType={EntityTypeName.PrAnnualTaxSettlement}
            page={annualTaxSettlement}/>,
        <PrivateRoute
            path={`${ROUTE_PAYROLL_YEAR_OPERATIONS}/:year?/:task?`}
            key={ROUTE_PAYROLL_YEAR_OPERATIONS}
            CPs={UserPermissionCodes.PrPayroll}
            component={PayrollYearOperation}/>,
        <PrivateRoute
            path={ROUTE_PAYROLL_OVERVIEW}
            key={ROUTE_PAYROLL_OVERVIEW}
            CPs={[UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll]}
            component={PayrollOverview}/>,
        <PrivateRoute
            path={BENEFIT_OVERVIEW_ROUTE}
            key={BENEFIT_OVERVIEW_ROUTE}
            CPs={[UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll]}
            component={BenefitOverview}/>,
        <PrivateRoute
            path={DEDUCTION_OVERVIEW_ROUTE}
            key={DEDUCTION_OVERVIEW_ROUTE}
            CPs={[UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll]}
            component={DeductionOverview}/>,
        <PrivateRoute
            path={HEALTH_INSURANCE_CONTRIBUTIONS_OVERVIEW_ROUTE}
            key={HEALTH_INSURANCE_CONTRIBUTIONS_OVERVIEW_ROUTE}
            CPs={[UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll]}
            component={HealthInsuranceContributionsOverview}/>,
        <PrivateRoute
            path={TAX_CREDITS_AND_ALLOWANCES_OVERVIEW_ROUTE}
            key={TAX_CREDITS_AND_ALLOWANCES_OVERVIEW_ROUTE}
            CPs={[UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll]}
            component={OverviewOfTaxCreditsAndTaxAllowances}/>,
        <PrivateRoute
            path={SOCIAL_SECURITY_CONTRIBUTIONS_OVERVIEW_ROUTE}
            key={SOCIAL_SECURITY_CONTRIBUTIONS_OVERVIEW_ROUTE}
            CPs={[UserPermissionCodes.Reports, UserPermissionCodes.PrPayroll]}
            component={SocialSecurityContributionsOverview}/>,
    ];
}